import _ from 'lodash';
import { templateBuilder, formatTemplate } from 'boost-template-engine';

const INCLUDE_PREFIX = 'render';
const INCLUDE_REGEX = new RegExp(`{%\\s*${INCLUDE_PREFIX}\\s*[^{}]*%}`, 'g');
const EXTRACT_PATH_LIQUID = new RegExp(`{%\\s*${INCLUDE_PREFIX}\\s*(.*)\\s*%}`);

export const combineWidgets = async (
  mainWidgetName,
  widgets,
  settings,
  compiledSettingsCss,
  themeNameLib,
  themeLibVersion
) => {
  const templateTree = [];
  // Process widget template
  for (const widget of widgets) {
    // console.log('process', JSON.stringify(widget, null, 2));
    const { widgetId, widgetName, widgetTemplate } = widget;

    const regexParsing = widgetTemplate.match(INCLUDE_REGEX) || [];

    // console.log('regexParsing', JSON.stringify(regexParsing, null, 2));

    const node = { widgetId, widgetName, children: [] };
    if (_.isEmpty(regexParsing)) {
      templateTree.push(node);
      continue;
    }

    // Find related render widget
    const children = [];
    for (const includeString of regexParsing) {
      const relatedWidget = findWidget(widget, widgets, includeString);

      if (_.isEmpty(relatedWidget)) {
        // console.log(`combineWidgets() Cannot find related widget for `, includeString);
        continue;
      }

      // Replace {% render <liquidPath> %} to {% render <RefCodeTag> %}

      const childNode = {
        widgetId: relatedWidget?.widgetId,
        widgetName: relatedWidget?.widgetName,
        originalInclude: includeString,
        refRenderTag: `RefRenderTag_${_.random(0, 9999)}`
      };

      children.push(childNode);
    }

    node.children = children;

    templateTree.push(node);
  }

  // console.log('=== templateTree', JSON.stringify(templateTree, null, 2));

  const compiledTemplate = await buildWidgets(mainWidgetName, widgets, templateTree, settings);
  let version = process.env.REACT_APP_BOOST_CDN_FOLDER;

  if (process.env.REACT_APP_ENV === 'production') {
    version = themeLibVersion;
  }
  const host = process.env.REACT_APP_BOOST_CDN_URL || 'https://boost-cdn-staging.bc-solutions.net';

  const html = `
      <!DOCTYPE html>
      <html lang="en">
        <head>
          <meta charset="UTF-8" />
          <meta name="viewport" content="width=device-width, initial-scale=1.0" />
          <title>Widget integration</title>
          <link href=${`${host}/widget-integration/theme/${themeNameLib}/${version}/main.css`} rel="stylesheet" type="text/css" media="all" data-app-id="8900771841">
        </head>
        <body>
        ${
          !_.isEmpty(compiledSettingsCss) &&
          `<style>
            ${compiledSettingsCss}
          </style>
          `
        }
          ${compiledTemplate}
        </body>
        </html>
          `;
  return html;
};

const findWidget = (widget, allWidgets, includeString) => {
  const { widgetElements: widgetElementIds = [], sourceLiquid } = widget;

  const matches = includeString.match(EXTRACT_PATH_LIQUID) || [];

  // Throw error
  if (matches.length < 2) {
    throw new Error(`Cannot get included render liquid ${includeString}`);
  }

  // <!> Fragile point
  // Convert from related path to absolute path based on source liquid of current widget
  const renderIncludePath = matches[1].trim().replace(/'/g, '').replace(/"/g, '');
  const absoluteLiquid = absolutePath(sourceLiquid, renderIncludePath);
  // console.log('absoluteLiquid', absoluteLiquid);

  // Find related widget for each including render
  let relatedWidget = {};
  for (const widgetId of widgetElementIds) {
    const widgetDetails = _.find(allWidgets, { widgetId });

    // Found related widget
    if (widgetDetails && widgetDetails.sourceLiquid === absoluteLiquid) {
      relatedWidget = widgetDetails;
      break;
    }
  }

  // console.log(`=> Found widget: ${relatedWidget?.widgetId}`);

  return relatedWidget;
};

export const absolutePath = (basePath, relativePath) => {
  const stack = basePath.split('/');
  const parts = relativePath.split('/');
  stack.pop(); // remove current file name (or empty string)
  // (omit if "base" is the current folder without trailing slash)

  for (let i = 0; i < parts.length; i++) {
    if (parts[i] === '.') continue;
    if (parts[i] === '..') stack.pop();
    else stack.push(parts[i]);
  }

  return stack.join('/');
};

const buildWidgets = async (mainWidgetName, allWidgets, templateTree, settings) => {
  const mainWidget = _.find(allWidgets, { widgetName: mainWidgetName });
  if (!mainWidget) {
    throw new Error(`buildWidgets() - Main widget doesn't exist!`);
  }

  const renderParamsTemplate = {};

  // Replace render tag
  const { widgetTemplate, customizeTemplate, hasCustomized } = mainWidget;
  let newWidgetTemplate =
    hasCustomized && !_.isEmpty(customizeTemplate) ? customizeTemplate : widgetTemplate;
  const templateTreeMainNode = _.find(templateTree, { widgetId: mainWidget.widgetId }) || {};
  const children = _.get(templateTreeMainNode, 'children', []);

  for (const element of children) {
    // Check if this element having children in template tree
    const { widgetId, originalInclude, refRenderTag } = element;
    const templateElement = _.find(templateTree, { widgetId });

    // TODO
    if (!templateElement) {
      // console.log(`buildWidgets() Cannot find element in template => Maybe issue!`);
      continue;
    }

    const templateElementChildren = _.get(templateElement, 'children', []);
    if (_.isArray(templateElementChildren) && templateElementChildren.length > 0) {
      // console.log(
      //   `buildWidgets() Widget ${templateElement?.widgetName} having children => Process later!`,
      // );

      // Call recursive to build sub-element template having children
      const compiledTemplateSubElement = await buildWidgets(
        templateElement?.widgetName,
        allWidgets,
        templateTree,
        settings
      );

      // Replace render tag
      newWidgetTemplate = newWidgetTemplate.replace(
        `${originalInclude}`,
        `{% render ${refRenderTag} %}`
      );

      // Set render params with compiled sub template
      _.set(renderParamsTemplate, refRenderTag, compiledTemplateSubElement);
      continue;
    }

    // Replace render tag
    newWidgetTemplate = newWidgetTemplate.replace(
      `${originalInclude}`,
      `{% render ${refRenderTag} %}`
    );

    const elementWidget = _.find(allWidgets, { widgetId }) || {};
    _.set(renderParamsTemplate, refRenderTag, elementWidget?.widgetTemplate);
  }

  // console.log(`Original template`, widgetTemplate);
  // console.log(`New template`, newWidgetTemplate);
  // console.log(`Widget params`, JSON.stringify(renderParamsTemplate, null, 2));

  // Render compiled template
  let compiledTemplate = '';
  const combineStoreSettings = settings;

  try {
    compiledTemplate = templateBuilder(
      newWidgetTemplate,
      combineStoreSettings,
      renderParamsTemplate
    );
  } catch (error) {
    console.log(`Error: buildWidgets() ${mainWidgetName} - Error: `, error.stack);
  }

  // console.log('=== compiledTemplate', compiledTemplate);

  // Format template
  const prettierCompiledTemplate = formatTemplate(compiledTemplate);

  return prettierCompiledTemplate;
};

export const detectLoops = liquidTemplate => {
  const loopPatterns = [
    /\{%\s*for\s+\w+\s+in\s+.+?\s*%\}/g, // For loop
    /\{%\s*while\s+.+?\s*%\}/g // While loop
  ];

  const loopTypes = ['For loop', 'While loop'];
  let detectedLoops = [];

  loopPatterns.forEach((pattern, index) => {
    const matches = liquidTemplate.match(pattern);
    if (matches) {
      matches.forEach(match => {
        detectedLoops.push({
          type: loopTypes[index],
          content: match
        });
      });
    }
  });

  return detectedLoops;
};

export const bytesToMB = bytes => {
  return bytes / (1024 * 1024);
};

export const calculateDocSize = doc => {
  return Buffer.from(String(doc)).length;
};
